import Button, { ButtonHTMLTag, ButtonStyle } from '@/app/components/Button/Button';
import { useIsUserInFloatingButtonGroup } from '@/app/hooks/useIsUserInFloatingButtonGroup';
import { useIsProjectActive, useIsProjectPublic } from '@/app/providers/ProjectProvider';
import { PropsWithClassname } from '@/app/types';
import { formatPriceInCents } from '@/app/utils/formatting';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

export type DonateBoxButtonsProps = PropsWithClassname & {
  donateLink: string;
  activeGoodHabitAmount: number;
  onShareButtonClick: () => void;
  shouldBeButtonsLarge?: boolean;
  shareButtonText: string;
  donateButtonTestId?: string;
};
export const DonateBoxButtons = forwardRef<HTMLDivElement, DonateBoxButtonsProps>(
  (
    {
      donateLink,
      activeGoodHabitAmount,
      onShareButtonClick,
      className,
      shouldBeButtonsLarge,
      shareButtonText,
      donateButtonTestId,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const isProjectPublic = useIsProjectPublic();
    const isProjectActive = useIsProjectActive();
    const isUserInFloatingButtonGroup = useIsUserInFloatingButtonGroup();

    return (
      <div
        ref={ref}
        className={classNames(className, 'flex lg:flex-col gap-[16px] lg:gap-0', {
          'flex-row-reverse lg:flex-col': isUserInFloatingButtonGroup,
        })}
      >
        <Button
          testId={donateButtonTestId}
          style={ButtonStyle.PRIMARY}
          className={classNames(
            'grow text-center lg:mb-[16px] lg:px-[24px] lg:py-[20px] tracking-[4px]',
            shouldBeButtonsLarge && 'leading-[32px]',
          )}
          tagSpecificProps={{ htmlTag: ButtonHTMLTag.LINK, href: donateLink, disabled: !isProjectActive }}
        >
          {t(activeGoodHabitAmount ? 'components.DonateBox.donateAmount' : 'components.DonateBox.donate', {
            amount: formatPriceInCents(activeGoodHabitAmount ?? 0),
          })}
        </Button>
        <Button
          testId="share-button"
          style={ButtonStyle.PRIMARY_OUTLINED}
          className={classNames(
            'grow-0 lg:px-[24px] lg:py-[20px] tracking-[4px]',
            shouldBeButtonsLarge && 'leading-[32px]',
          )}
          tagSpecificProps={{ htmlTag: ButtonHTMLTag.BUTTON, onClick: onShareButtonClick, disabled: !isProjectPublic }}
        >
          {shareButtonText}
        </Button>
      </div>
    );
  },
);

DonateBoxButtons.displayName = 'DonateBoxButtons';
